import { FC, ReactNode, useMemo } from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import {
  GRUPO_TOPO,
  ItemFormulario,
  RespostaFormulario,
  SchemaFormulario,
  ValorResposta,
} from '@/types/formulario'
import {
  indexarRespostas,
  itemVisivel,
  quantidadeItensGrupo,
  resolvedor,
  valorComoTexto,
  valorResposta,
} from '@/utils/formularioCondicao'
import { formatCNPJ, formatCPF, formatCurrency, formatDate } from '@/utils/formatters'

export interface RespostasFormularioLeituraProps {
  schema: SchemaFormulario
  respostas: RespostaFormulario[]
  /** Texto para campo visível sem resposta. */
  vazio?: string
}

/** Valor legível de uma resposta, pelo tipo do campo. */
export const formatarValorFormulario = (item: ItemFormulario, valor: ValorResposta | undefined): string | null => {
  const texto = valorComoTexto(valor)
  if (texto == null) return null
  switch (item.tipo) {
    case 'SIM_NAO':
      return texto === 'true' ? 'Sim' : texto === 'false' ? 'Não' : texto
    case 'OPCAO':
      return item.opcoes?.find((o) => o.valor === texto)?.rotulo ?? texto
    case 'MOEDA':
      return Number.isFinite(Number(texto)) ? formatCurrency(Number(texto)) : texto
    case 'PERCENTUAL':
      return `${texto.replace('.', ',')}%`
    case 'DATA':
      return formatDate(texto)
    case 'CPF':
      return formatCPF(texto) || texto
    case 'CNPJ':
      return texto.length === 14 ? formatCNPJ(texto) : texto
    default:
      return texto
  }
}

const Par: FC<{ rotulo: string; valor: string | null; largo?: boolean; vazio: string }> = ({
  rotulo,
  valor,
  largo,
  vazio,
}) => (
  <Box sx={{ gridColumn: largo ? '1 / -1' : undefined }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
      {valor ?? vazio}
    </Typography>
  </Box>
)

const grade = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
  gap: 2,
} as const

/**
 * Leitura genérica das respostas de um formulário, PELA VERSÃO vinculada (AN-6 §3):
 * serve a versão nova de qualquer produto e a LEGADO (os 7 dados complementares da V39)
 * sem nenhum rótulo fixo na tela. Campo oculto por condição não aparece; grupos viram
 * cartões ("Sócio 1", "Sócio 2"…).
 */
export const RespostasFormularioLeitura: FC<RespostasFormularioLeituraProps> = ({
  schema,
  respostas,
  vazio = 'Não informado',
}) => {
  const indice = useMemo(() => indexarRespostas(respostas), [respostas])
  const topo = resolvedor(indice)

  const blocos: ReactNode[] = []
  let soltos: ReactNode[] = []
  const fecharSoltos = () => {
    if (soltos.length === 0) return
    blocos.push(
      <Box key={`soltos-${blocos.length}`} sx={grade}>
        {soltos}
      </Box>
    )
    soltos = []
  }

  for (const item of schema.itens ?? []) {
    if (!itemVisivel(item, topo)) continue
    if (item.tipo !== 'GRUPO') {
      soltos.push(
        <Par
          key={item.chave}
          rotulo={item.rotulo}
          valor={formatarValorFormulario(item, valorResposta(indice, GRUPO_TOPO, 0, item.chave))}
          largo={item.tipo === 'TEXTO_LONGO'}
          vazio={vazio}
        />
      )
      continue
    }
    fecharSoltos()
    const n = quantidadeItensGrupo(item, respostas)
    blocos.push(
      <Box key={`grupo-${item.chave}`} component="section" aria-label={item.rotulo}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
          {item.rotulo}
        </Typography>
        {n === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {vazio}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {Array.from({ length: n }, (_, i) => {
              const irmao = resolvedor(indice, item, i)
              const rotulo = item.repeatableGroup || item.quantidadeDe ? `${item.rotulo} ${i + 1}` : item.rotulo
              return (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }} role="group" aria-label={rotulo}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    {rotulo}
                  </Typography>
                  <Box sx={grade}>
                    {(item.campos ?? [])
                      .filter((c) => itemVisivel(c, irmao))
                      .map((c) => (
                        <Par
                          key={c.chave}
                          rotulo={c.rotulo}
                          valor={formatarValorFormulario(c, valorResposta(indice, item.chave, i, c.chave))}
                          largo={c.tipo === 'TEXTO_LONGO'}
                          vazio={vazio}
                        />
                      ))}
                  </Box>
                </Paper>
              )
            })}
          </Stack>
        )}
      </Box>
    )
  }
  fecharSoltos()

  return <Stack spacing={2}>{blocos}</Stack>
}

export default RespostasFormularioLeitura
